import { ArrowLeftOutlined, ClockCircleOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Form,
  Input,
  Layout,
  Radio,
  Result,
  Steps,
  Typography,
} from "antd";
import axios from "axios";
import React, { useState } from "react";
import { UNKNOWN_ERROR_MESSAGE } from "../constants";
import { persistLinkState } from "../hooks/useLinkTracker";
import { captureException } from "../sentry";
import Store from "../stateStore";
import { extractLinkParamsFromState, getBackendUrl, STORES } from "../utils";

const { Step } = Steps;
const { Paragraph, Text, Title } = Typography;

type Props = {
  clientSecret: string;
  onBack: () => any;
  onCancel: () => any;
  onComplete: (public_token: string) => any;
};

type VistaFields = {
  adminName: string;
  contactEmail: string;
  hosting: "cloud" | "on_prem";
  vistaServer: string;
  vistaDatabase: string;
  serviceUserName: string;
  serviceUserPassword: string;
  co: string;
  companies: string;
  custGroup: string;
  recType: string;
};

// Sent as-is: a password can legitimately start or end with a space.
const UNTRIMMED_FIELDS = new Set(["serviceUserPassword"]);

const SERVICE_USER_STEPS = [
  "In Vista, open Viewpoint Administration > Programs > VA User Profile and add a new record.",
  "Pick a username such as XChangeRutter, set User Type to 'User Application', and enter any email (it isn't used).",
  "Save. Vista warns that no SQL login matches the username: that's expected. Close the form and set a strong password.",
  "Give the user the datatype security groups for every company Rutter should sync.",
  "Only if Vista runs on your own server: grant the user's SQL login db_datareader and VCSUsers.",
];

const AGENTS_URL_PLACEHOLDER = "[link from Rutter's email]";

// Keep in sync with rutter-backend vistaAgentInstallCase (platforms/vista/onboarding.ts).
function supportCase(values: VistaFields) {
  return {
    fields: [
      ["Case Type", "Product Support & Account"],
      ["Product", "Vista/Vista Web"],
      ["Application", "Vista"],
      ["Support Category", "Azure Virtual Desktop"],
      ["Subject", "App Xchange Agent Install Request"],
    ],
    description: [
      "Please install the App Xchange Agent on our Vista server so our Vista <> Rutter integration in Trimble App Xchange can connect.",
      "",
      `Server name: ${values.vistaServer.trim()}`,
      `Vista database: ${values.vistaDatabase.trim()}`,
      `Service user name: ${values.serviceUserName.trim()}`,
      `App Xchange On-Prem Agents page: ${AGENTS_URL_PLACEHOLDER}`,
    ].join("\n"),
  };
}

// Digits only (surrounding spaces trimmed on submit); blank allowed when optional.
const INTEGER_RULE = {
  pattern: /^\s*\d+\s*$/,
  message: "Enter a whole number",
};

function Vista(props: Props) {
  const { onComplete } = props;
  const store = Store.useStore();
  const autoPlatform = store.get("autoPlatform");
  const usage = store.get("usage");
  const [form] = Form.useForm<VistaFields>();
  const [stepNumber, setStepNumber] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [publicToken, setPublicToken] = useState("");
  const [submitted, setSubmitted] = useState<VistaFields | null>(null);

  const submit = async (values: VistaFields) => {
    setLoading(true);
    setError("");
    persistLinkState(STORES.VISTA, store.getState());
    // Omit blank optional fields, matching GenericRoute's UserCreds body.
    const userInputs = Object.fromEntries(
      Object.entries(values)
        .map(([key, value]) => [
          key,
          UNTRIMMED_FIELDS.has(key) ? value ?? "" : (value ?? "").trim(),
        ])
        .filter(([, value]) => value !== ""),
    );
    try {
      const { data } = await axios.post(
        `${getBackendUrl()}/authentication/verify-credential`,
        {
          ...extractLinkParamsFromState(store),
          ...userInputs,
          platform: STORES.VISTA,
          usage,
        },
      );
      setSubmitted(values);
      setPublicToken(data.public_token);
    } catch (e: any) {
      captureException(e);
      setError(e.response?.data?.error_message ?? UNKNOWN_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const logo = (
    <div className="text-center mb-6">
      <Title level={3} className="!mb-0">
        Viewpoint Vista
      </Title>
      <Text type="secondary">Connected through Trimble App Xchange</Text>
    </div>
  );

  if (publicToken && submitted) {
    const isCloud = submitted.hosting === "cloud";
    const ticket = supportCase(submitted);
    return (
      <Layout className="bg-white w-full">
        <Layout.Content className="overflow-y-auto px-16">
          <div className="w-full flex justify-center">
            <div className="w-full max-w-[800px] py-8">
              {logo}
              <Result
                icon={<ClockCircleOutlined style={{ color: "#1890ff" }} />}
                title="Vista setup is in progress"
                subTitle={`Rutter is now creating your Trimble App Xchange workspace. Watch ${submitted.contactEmail.trim()} for two emails, then do the two steps below.`}
              />
              <Steps direction="vertical" size="small" current={-1}>
                <Step
                  status="wait"
                  title="Accept the Trimble App Xchange invite"
                  description={`Trimble emails "You're Invited to Use an Integration on App Xchange" to ${submitted.contactEmail.trim()}. Accept it.`}
                />
                <Step
                  status="wait"
                  title={
                    isCloud
                      ? "Ask Trimble Support to install the App Xchange Agent"
                      : "Install the App Xchange Agent on your Vista server"
                  }
                  description={
                    isCloud
                      ? "As soon as you've accepted, sign in to Trimble Support and open a case with the details below. Rutter's email gives you the one link that's missing. Trimble usually installs the agent in 1–3 business days."
                      : "Rutter's email includes the link to your workspace's activation code. Install the agent on the server that runs Vista with that code."
                  }
                />
              </Steps>
              {isCloud && (
                <div className="mb-6">
                  <Paragraph strong>Trimble Support case</Paragraph>
                  <ul className="list-none mb-2">
                    {ticket.fields.map(([label, value]) => (
                      <li key={label}>
                        <Text type="secondary">{label}: </Text>
                        <Text copyable>{value}</Text>
                      </li>
                    ))}
                  </ul>
                  <Text type="secondary">
                    Description (replace the bracketed part with the link from
                    Rutter's email):
                  </Text>
                  <Paragraph copyable={{ text: ticket.description }}>
                    <pre className="whitespace-pre-wrap m-0 font-sans">
                      {ticket.description}
                    </pre>
                  </Paragraph>
                </div>
              )}
              <Paragraph type="secondary">
                After that, Rutter connects everything and starts syncing your
                data. We'll email you when it's live. Click Finish to close this
                window.
              </Paragraph>
              <Button
                type="primary"
                block
                size="large"
                onClick={() => onComplete(publicToken)}
              >
                Finish
              </Button>
            </div>
          </div>
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <Layout className="bg-white w-full">
      <Layout.Content className="overflow-y-auto px-16">
        <div className="w-full flex justify-center">
          <div className="w-full max-w-[800px] py-8">
            {!autoPlatform && (
              <Button onClick={props.onBack} icon={<ArrowLeftOutlined />}>
                Back
              </Button>
            )}
            {logo}

            {stepNumber === 0 ? (
              <div>
                <Paragraph>
                  Connecting Vista takes a few days because Trimble sets up a
                  small agent on your Vista server. You fill in one form here
                  (best done by your Vista administrator) and Rutter does the
                  rest. Afterwards you only accept an invite and send one
                  request to Trimble Support, which we write for you.
                </Paragraph>
                <Paragraph strong>
                  Before you continue, create a Vista user for Rutter:
                </Paragraph>
                <ol className="list-decimal list-inside mb-4">
                  {SERVICE_USER_STEPS.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ol>
                <Paragraph>
                  You'll also need the Vista server and database name (your IT
                  team or Trimble Support can tell you).
                </Paragraph>
                <Button
                  type="primary"
                  block
                  size="large"
                  onClick={() => setStepNumber(1)}
                >
                  I've created the user, continue
                </Button>
              </div>
            ) : (
              <Form
                form={form}
                layout="vertical"
                requiredMark="optional"
                onFinish={submit}
              >
                <Form.Item
                  label="Your name"
                  name="adminName"
                  rules={[{ required: true, message: "Enter your name" }]}
                >
                  <Input placeholder="Jane Smith" />
                </Form.Item>
                <Form.Item
                  label="Your email"
                  name="contactEmail"
                  extra="Trimble sends the App Xchange invite here, and Rutter emails you the link for the Trimble Support request."
                  rules={[
                    { required: true, message: "Enter your email" },
                    { type: "email", message: "Enter a valid email" },
                  ]}
                >
                  <Input type="email" placeholder="admin@company.com" />
                </Form.Item>
                <Form.Item
                  label="Where does Vista run?"
                  name="hosting"
                  rules={[{ required: true, message: "Choose one" }]}
                >
                  <Radio.Group>
                    <Radio value="cloud">
                      Viewpoint Cloud (hosted by Trimble)
                    </Radio>
                    <Radio value="on_prem">Our own server</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  label="Vista server name"
                  name="vistaServer"
                  rules={[{ required: true, message: "Enter the server name" }]}
                >
                  <Input placeholder="VPCLOUD-SQL01" />
                </Form.Item>
                <Form.Item
                  label="Vista database name"
                  name="vistaDatabase"
                  rules={[
                    { required: true, message: "Enter the database name" },
                  ]}
                >
                  <Input placeholder="Viewpoint" />
                </Form.Item>
                <Form.Item
                  label="Service user name"
                  name="serviceUserName"
                  extra="The Vista user you just created for Rutter."
                  rules={[
                    { required: true, message: "Enter the service user name" },
                  ]}
                >
                  <Input placeholder="XChangeRutter" autoComplete="off" />
                </Form.Item>
                <Form.Item
                  label="Service user password"
                  name="serviceUserPassword"
                  extra="Used only to configure your App Xchange connection."
                  rules={[{ required: true, message: "Enter the password" }]}
                >
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Form.Item
                  label="Vista company number for invoices"
                  name="co"
                  extra="The company (Co) Rutter posts invoices to."
                  rules={[
                    { required: true, message: "Enter the company number" },
                    INTEGER_RULE,
                  ]}
                >
                  <Input inputMode="numeric" placeholder="1" />
                </Form.Item>
                <Form.Item
                  label="Other companies to sync"
                  name="companies"
                  extra="Other Vista company numbers to read data from, separated by commas. Leave blank for just the one above."
                  rules={[
                    {
                      pattern: /^[\s\d,]*$/,
                      message: "Whole numbers separated by commas",
                    },
                  ]}
                >
                  <Input placeholder="2, 3" />
                </Form.Item>
                <Form.Item
                  label="Customer group"
                  name="custGroup"
                  extra="Leave blank to use customer group 1."
                  rules={[INTEGER_RULE]}
                >
                  <Input inputMode="numeric" placeholder="1" />
                </Form.Item>
                <Form.Item
                  label="Receivable type"
                  name="recType"
                  extra="Leave blank if you are not sure."
                  rules={[INTEGER_RULE]}
                >
                  <Input inputMode="numeric" />
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={loading}
                >
                  Submit
                </Button>
                <Button
                  type="link"
                  block
                  className="mt-2"
                  onClick={() => setStepNumber(0)}
                >
                  Back to overview
                </Button>
              </Form>
            )}

            {error && (
              <Alert
                className="mt-4"
                type="error"
                message="Unable to start Vista setup"
                description={error}
                showIcon
              />
            )}
          </div>
        </div>
      </Layout.Content>
    </Layout>
  );
}

export default Vista;
